import { useTitle } from '@/lib/useTitle';
import { useJsonLd } from '@/lib/useJsonLd';
import { faqPageJsonLd } from '@/lib/seo';
import LocalPageSheet from '@/components/LocalPageSheet';
import type { Faq } from '@/components/LocalPageSheet';

export const meta = {
  path: '/commercial-property-valuation-waterloo-region-guelph',
  title: 'Commercial Property Valuation — Waterloo Region & Guelph',
  description:
    'Commercial property valuation guidance for owners in Cambridge, Kitchener, Waterloo and Guelph. Review income, tenancy, comparable sales and positioning before deciding whether to sell.',
};

const FAQS: Faq[] = [
  {
    q: 'How is a commercial property value estimated?',
    a: 'The method depends on the asset. Income-producing properties are often reviewed using net operating income, lease terms and market capitalization rates, while owner-occupied properties may lean more heavily on comparable sales, replacement economics and the utility of the site and building.',
  },
  {
    q: 'What should I have ready before asking for a valuation?',
    a: 'Useful starting documents include the rent roll, leases and amendments, property-tax information, operating expenses, recent capital work, building and land size, zoning information and any environmental or building reports you already have. Missing documents do not prevent a first conversation.',
  },
  {
    q: 'Is this the same as a formal appraisal?',
    a: 'No. A broker opinion of value or pricing analysis is used to help an owner understand market positioning and a possible sale strategy. A lender, court, tax authority or other party may require a formal appraisal from a qualified appraiser instead.',
  },
  {
    q: 'Can you review a property even if I am not ready to list it?',
    a: 'Yes. Owners often want to understand the likely buyer pool, what information would matter in due diligence, and what could affect pricing before deciding whether a sale makes sense.',
  },
  {
    q: 'Do you cover Cambridge, Kitchener, Waterloo and Guelph?',
    a: 'Yes. The practice covers commercial sales, leasing and business listings across those four cities, with the analysis adjusted to the property type, location, tenancy and current comparable activity.',
  },
];

export default function CommercialPropertyValuation() {
  useTitle(meta.title, meta.description);
  useJsonLd('faq-commercial-property-valuation', faqPageJsonLd(FAQS));

  return (
    <LocalPageSheet
      sheetNo="S-06"
      kicker="Owner search — value before you sell"
      h1="What is my commercial property worth?"
      intro="A useful commercial valuation starts with the property itself — income, leases, building and land, zoning, condition and the buyers who would realistically compete for it — not a residential-style online estimate."
      glyph="office"
      audiences={[
        {
          label: 'Considering a sale',
          body: 'Start with the property type, address, approximate size and whether it is owner-occupied or tenanted. From there we can work through the information that will actually affect pricing and buyer demand.',
          ctaLabel: 'Review my property',
        },
        {
          label: 'Not ready to sell yet',
          body: 'A pre-sale review can still identify lease issues, missing documents, deferred work or positioning questions that may matter later. The point is to understand the property before a listing decision is made.',
          ctaLabel: 'Start with a valuation conversation',
        },
      ]}
      localLabel="Cambridge · Kitchener · Waterloo · Guelph"
      localBody={
        <>
          <p>
            Commercial value is local and asset-specific. An industrial building near the 401 is not valued the same way as a downtown retail property, a small office building, a mixed-use investment or development land.
          </p>
          <p>
            For tenanted property, the lease structure and remaining term can be as important as the bricks. For owner-occupied property, buyers may focus more on location, functional layout, loading, parking, clear height, zoning and how difficult the building would be to replace.
          </p>
          <p>
            The goal is not to manufacture a number. It is to understand the evidence a buyer will use when deciding what the property is worth to them.
          </p>
        </>
      }
      honestLabel="Not an instant-estimate gimmick"
      honestBody="Commercial properties are too different for a responsible one-click valuation. A useful number requires real property information and current market evidence. If a formal appraisal is required for lending, legal, tax or another purpose, that is a different assignment from brokerage pricing advice."
      faqs={FAQS}
      interest="buying-selling"
      closingLine="Know the property before deciding whether to sell it."
      related={[
        { to: '/sell-my-commercial-property-kitchener-waterloo', label: 'Selling commercial property — Kitchener-Waterloo' },
        { to: '/buying-selling', label: 'Full commercial sales services' },
        { to: '/lease-my-commercial-property-waterloo-region-guelph', label: 'Have vacant space instead?' },
      ]}
    />
  );
}
